import { useState, useMemo } from "react";
import { useBranding } from "@/data/BrandingContext";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { SignatureCapture } from "@/components/SignatureCapture";
import { deriveBuildSummary, createConfigSnapshot } from "@/data/buildConfigMapping";
import type { Quote, ContractDocument, CancelReason } from "@/data/quoteTypes";
import { CANCEL_REASON_LABELS } from "@/data/quoteTypes";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  FileText, CheckCircle, Shield, User, Building2, Pen, AlertTriangle, XCircle, Palette,
} from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Quote;
  finalTotal: number;
  onContractApproved: (contract: ContractDocument) => void;
  onContractCancelled?: (contract: ContractDocument) => void;
}

export function ContractApprovalDialog({ open, onOpenChange, quote, finalTotal, onContractApproved, onContractCancelled }: Props) {
  const { branding } = useBranding();
  const { options: allOptions } = useProductOptions();
  const [homeownerSig, setHomeownerSig] = useState("");
  const [repSig, setRepSig] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rescissionAcknowledged, setRescissionAcknowledged] = useState(false);
  const [selectionsApproved, setSelectionsApproved] = useState(false);
  const [showBuildConfirm, setShowBuildConfirm] = useState(false);

  const verification = quote.verification;
  const cfg = quote.projectConfig;

  const canApprove = homeownerSig && repSig && termsAccepted && rescissionAcknowledged && selectionsApproved;

  // Key selections derived from centralized build mapping (single source of truth)
  const keySelections = useMemo(() => {
    if (!cfg) return [];
    return deriveBuildSummary(cfg, allOptions).map(item => ({
      label: item.label,
      value: item.value,
    }));
  }, [cfg, allOptions]);

  // Build confirmation common items
  const buildChecklist = [
    "Grab bars included if needed",
    "All accessory selections complete",
    "Color and material selections confirmed",
    "All add-ons reviewed",
  ];

  const handleApprove = () => {
    if (!canApprove) return;
    // Show build confirmation first
    setShowBuildConfirm(true);
  };

  const handleFinalApprove = () => {
    const now = new Date().toISOString();
    const snapshot = cfg ? createConfigSnapshot(cfg) : null;
    const contract: ContractDocument = {
      id: quote?.displayReference ? `CTR-${quote.displayReference.replace(/^QT-/i, "")}` : quote?.id ? `CTR-${quote.id.slice(0, 8)}` : `CTR-000`,
      quoteId: quote.id,
      customerName: quote.customerName,
      customerEmail: quote.customerEmail,
      customerAddress: quote.customerAddress,
      contractTotal: finalTotal,
      homeownerSignature: homeownerSig,
      homeownerSignedAt: now,
      repSignature: repSig,
      repSignedAt: now,
      termsAccepted: true,
      rescissionAcknowledged: true,
      status: "Signed",
      createdAt: now,
      approvedAt: now,
      ownershipType: verification?.ownershipType === "POA" ? "POA" : "Homeowner",
      poaDocumentUploaded: verification?.poaDocumentUploaded || false,
      hoaInvolved: verification?.hoaInvolved || false,
      selectedOptionsApproved: true,
      configSnapshotHash: snapshot?.hash,
      configSnapshotAt: snapshot?.snapshotAt,
    };

    onContractApproved(contract);
    toast.success("Contract signed and approved!");
    setShowBuildConfirm(false);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open && !showBuildConfirm} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" /> Contract Approval & Signature
            </DialogTitle>
            <DialogDescription>
              Review the agreement details, confirm selections, capture signatures, and approve.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-2">
            {/* ── Header Section ── */}
            <div className="rounded-lg border bg-muted/20 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  <span className="text-sm font-bold">{branding.companyName}</span>
                </div>
                <Badge variant="outline" className="text-[10px]">{quote.displayReference || quote.id}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{quote.customerName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Address</p>
                  <p className="font-medium">{quote.customerAddress || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Ownership</p>
                  <p className="font-medium">{verification?.ownershipType || "Homeowner"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">HOA</p>
                  <p className="font-medium">{verification?.hoaInvolved ? "Yes — Flagged" : "No"}</p>
                </div>
                {verification?.hasSecondaryContact && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Secondary Contact</p>
                    <p className="font-medium">{verification.secondaryFirstName} {verification.secondaryLastName} {verification.secondaryPhone && `· ${verification.secondaryPhone}`}</p>
                  </div>
                )}
                <div>
                  <p className="text-muted-foreground">Contract Total</p>
                  <p className="font-bold text-primary text-base">${finalTotal.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* ── Build / Work Order Section ── */}
            {cfg?.bathroomSystemType && (
              <div className="rounded-lg border p-4 space-y-2">
                <div className="flex items-center gap-2 pb-1 border-b">
                  <Palette className="h-3.5 w-3.5 text-muted-foreground" />
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Build / Work Order</h3>
                </div>
                <div className="text-xs space-y-1">
                  <p>{cfg.bathroomSystemType} — {cfg.bathroomMaterials?.join(", ")}</p>
                  {cfg.baseWidth && <p>Dimensions: {cfg.baseWidth}" × {cfg.baseLength}"</p>}
                </div>
              </div>
            )}

            {/* ── Key Selections Approval ── */}
            {keySelections.length > 0 && (
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center gap-2 pb-1 border-b">
                  <CheckCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Confirm Selections</h3>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {keySelections.map((s) => (
                    <div key={s.label} className="flex justify-between py-1">
                      <span className="text-muted-foreground">{s.label}</span>
                      <span className="font-medium text-right max-w-[55%]">{s.value}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg border bg-primary/5">
                  <Checkbox id="selections" checked={selectionsApproved}
                    onCheckedChange={(c) => setSelectionsApproved(!!c)} />
                  <label htmlFor="selections" className="text-xs leading-relaxed cursor-pointer">
                    I confirm the colors, materials, and selected options listed above are correct.
                  </label>
                </div>
              </div>
            )}
            {keySelections.length === 0 && (
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox id="selections" checked={selectionsApproved}
                  onCheckedChange={(c) => setSelectionsApproved(!!c)} />
                <label htmlFor="selections" className="text-xs leading-relaxed cursor-pointer">
                  I confirm the project scope and selections are correct.
                </label>
              </div>
            )}

            {/* ── Terms ── */}
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox id="terms" checked={termsAccepted}
                  onCheckedChange={(c) => setTermsAccepted(!!c)} />
                <label htmlFor="terms" className="text-xs leading-relaxed cursor-pointer">
                  I agree to the terms and conditions of this agreement. I understand the scope of work described above
                  and authorize {branding.companyName} to perform the work at the stated price.
                </label>
              </div>
              <div className="flex items-start gap-3 p-3 rounded-lg border">
                <Checkbox id="rescission" checked={rescissionAcknowledged}
                  onCheckedChange={(c) => setRescissionAcknowledged(!!c)} />
                <label htmlFor="rescission" className="text-xs leading-relaxed cursor-pointer">
                  I acknowledge the 3-business-day right of rescission. I may cancel this agreement within 3 business
                  days of signing without penalty.
                </label>
              </div>
            </div>

            {/* ── Signatures ── */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 pb-1 border-b">
                <Pen className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Signatures</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <SignatureCapture label="Homeowner Signature" onCapture={setHomeownerSig} existingSignature={homeownerSig} />
                  {homeownerSig && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-success">
                      <CheckCircle className="h-3 w-3" /> Captured
                    </div>
                  )}
                </div>
                <div>
                  <SignatureCapture label="Company Representative" onCapture={setRepSig} existingSignature={repSig} />
                  {repSig && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-success">
                      <CheckCircle className="h-3 w-3" /> Captured
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Security note */}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
              <Shield className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Contract documents are securely stored and tied to the customer profile and job record.
                Documents are viewable within the portal. Downloads are restricted to authorized roles only.
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="gap-1.5 bg-success hover:bg-success"
              onClick={handleApprove}
              disabled={!canApprove}
            >
              <CheckCircle className="h-3.5 w-3.5" /> Approve & Sign Contract
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Build Confirmation Dialog ── */}
      <Dialog open={showBuildConfirm} onOpenChange={setShowBuildConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-warning" /> Design Consultant — Verify Build
            </DialogTitle>
            <DialogDescription>
              Before finalizing, confirm the build is complete and nothing is missing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {buildChecklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <CheckCircle className="h-3.5 w-3.5 text-success" />
                <span>{item}</span>
              </div>
            ))}
            <div className="p-2 rounded bg-warning/10 border border-warning/20 text-[10px] text-warning">
              If any common items (grab bars, accessories, etc.) are missing, go back and update the configuration before signing.
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowBuildConfirm(false)}>Go Back & Review</Button>
            <Button className="gap-1.5 bg-success hover:bg-success" onClick={handleFinalApprove}>
              <CheckCircle className="h-3.5 w-3.5" /> Confirmed — Finalize Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ── Contract Cancellation Dialog ── */
interface CancelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: (reason: CancelReason, customReason?: string) => void;
}

export function ContractCancelDialog({ open, onOpenChange, onCancel }: CancelProps) {
  const [reason, setReason] = useState<CancelReason | "">("");
  const [customReason, setCustomReason] = useState("");

  const canSubmit = reason && (reason !== "other" || customReason.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-destructive" /> Cancel Contract
          </DialogTitle>
          <DialogDescription>
            Select a reason for cancellation. A follow-up notification will be sent to the team.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label className="text-xs">Cancellation Reason *</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as CancelReason)}>
              <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Select reason…" /></SelectTrigger>
              <SelectContent>
                {(Object.entries(CANCEL_REASON_LABELS) as [CancelReason, string][]).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {reason === "other" && (
            <div>
              <Label className="text-xs">Please specify</Label>
              <Textarea className="mt-1 text-xs" rows={2} value={customReason}
                onChange={(e) => setCustomReason(e.target.value)} placeholder="Describe the reason…" />
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Keep Contract</Button>
          <Button variant="destructive" className="gap-1.5" onClick={() => {
            if (canSubmit) {
              onCancel(reason as CancelReason, reason === "other" ? customReason : undefined);
              onOpenChange(false);
              setReason("");
              setCustomReason("");
            }
          }} disabled={!canSubmit}>
            <XCircle className="h-3.5 w-3.5" /> Confirm Cancellation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
